import { Buffer } from "buffer";
import { encodeBearerAuthMetadata, encodeCompositeMetadata, encodeRoute } from "rsocket-composite-metadata";
import { Cancellable, OnExtensionSubscriber, Payload, Requestable } from "rsocket-core";
import { TopicMessageHandler, TopicSubscription } from "../types";
import { RSocketConnectionManager, RSocketTopicSubscriptionService } from "./utils";

type RSocketSubscription = Requestable & Cancellable & OnExtensionSubscriber;

// Initially request Int.MAX_VALUE (not Number.MAX_SAFE_INTEGER) items, which will (likely) never be reached
// before a refresh happens.
const DEFAULT_REQUEST_N = 2147483647;
const CLOSED_UNEXPECTEDLY_ERROR_MESSAGE = "WebsocketDuplexConnection: Socket closed unexpectedly";

/**
 * Creates a functional RSocket topic subscription service
 * @param connectionManager The RSocket connection manager instance
 * @param onMessageCountUpdate Optional callback for message count updates
 * @param onSubscriptionsChange Optional callback for subscription list changes
 * @returns RSocket topic subscription service
 */
export function createTopicSubscriptionService(
    connectionManager: RSocketConnectionManager,
    onMessageCountUpdate?: (count: number) => void,
    onSubscriptionsChange?: (topics: string[]) => void
): RSocketTopicSubscriptionService {
    const subscriptions = new Map<
        string,
        {
            subscription: RSocketSubscription;
            handlers: Set<TopicMessageHandler<unknown>>;
        }
    >();
    let messagesReceived = 0;
    let messageCountCallback: ((count: number) => void) | null = onMessageCountUpdate || null;

    const updateMessageCount = (): void => {
        messagesReceived++;
        if (messageCountCallback) {
            messageCountCallback(messagesReceived);
        }
    };

    const getMessageCount = (): number => {
        return messagesReceived;
    };

    const setMessageCountCallback = (callback: (count: number) => void): void => {
        messageCountCallback = callback;
    };

    const subscriptionsChangeCallback: ((topics: string[]) => void) | null = onSubscriptionsChange || null;

    const notifySubscriptionsChange = (): void => {
        if (subscriptionsChangeCallback) {
            subscriptionsChangeCallback([...subscriptions.keys()]);
        }
    };

    const getCurrentSubscriptions = (): string[] => {
        return [...subscriptions.keys()];
    };

    const createTopicSubscription = (topicName: string, onMessage: TopicMessageHandler<unknown>): TopicSubscription => ({
        isSubscribed: () => {
            const sub = subscriptions.get(topicName);
            return sub ? sub.handlers.has(onMessage) : false;
        },
        cancel: () => {
            const sub = subscriptions.get(topicName);
            if (sub) {
                sub.handlers.delete(onMessage);
                if (sub.handlers.size === 0) {
                    sub.subscription.cancel();
                    subscriptions.delete(topicName);
                    notifySubscriptionsChange();
                }
            }
        },
    });

    const subscribe = async <T>(topicName: string, onMessage: TopicMessageHandler<T>, authToken: string): Promise<TopicSubscription> => {
        try {
            if (!connectionManager.isConnected()) {
                await connectionManager.connect();
            }
            const socket = connectionManager.getSocket();
            const existingSubscription = subscriptions.get(topicName);

            if (existingSubscription) {
                existingSubscription.handlers.add(onMessage as TopicMessageHandler<unknown>);
                return createTopicSubscription(topicName, onMessage as TopicMessageHandler<unknown>);
            }

            const authMetadata = encodeBearerAuthMetadata(authToken);
            const routingMetadata = encodeRoute(topicName);

            // Combine them into one composite metadata buffer
            const compositeMetadata = encodeCompositeMetadata([
                ["message/x.rsocket.authentication.v0", authMetadata],
                ["message/x.rsocket.routing.v0", routingMetadata],
            ]);

            console.debug(`Subscribing to RSocket topic: ${topicName}`);
            const rsocketSubscription = socket.requestStream(
                {
                    data: Buffer.from(JSON.stringify({ message: "subscribe" })),
                    metadata: compositeMetadata,
                },
                DEFAULT_REQUEST_N,
                {
                    onNext: (payload: Payload) => {
                        try {
                            updateMessageCount();

                            let parsedData: T;
                            if (Buffer.isBuffer(payload.data) || (payload.data as unknown) instanceof Uint8Array) {
                                const decoder = new TextDecoder();
                                parsedData = JSON.parse(decoder.decode(payload.data as AllowSharedBufferSource)) as T;
                            } else if (typeof payload.data === "string") {
                                parsedData = JSON.parse(payload.data) as T;
                            } else {
                                parsedData = payload.data as T;
                            }

                            // Get the subscription and notify all handlers
                            const sub = subscriptions.get(topicName);
                            if (sub) {
                                sub.handlers.forEach((handler) => {
                                    try {
                                        handler(parsedData);
                                    } catch (handlerError) {
                                        console.error(`Error in topic handler for ${topicName}:`, handlerError);
                                    }
                                });
                            }
                        } catch (error) {
                            console.error(`Error parsing stream data for topic ${topicName}:`, error);
                            if (payload.data) {
                                console.debug("Raw data:", payload.data.toString ? payload.data.toString() : payload.data);
                            }
                        }
                    },
                    onError: (error: Error) => {
                        if (error.message.includes(CLOSED_UNEXPECTEDLY_ERROR_MESSAGE)) {
                            console.debug(
                                `Stream for topic ${topicName} closed unexpectedly, probably because we refreshed or navigated away`
                            );
                        } else {
                            console.error(`Stream failed for topic ${topicName}:`, error);
                        }
                        subscriptions.delete(topicName);
                    },
                    onComplete: () => {
                        console.debug(`Stream completed for topic ${topicName}`);
                        subscriptions.delete(topicName);
                    },
                    onExtension: () => {
                        console.debug(`Stream extension for topic ${topicName}`);
                    },
                }
            );

            // Store the subscription and handler
            subscriptions.set(topicName, {
                subscription: rsocketSubscription,
                handlers: new Set([onMessage as TopicMessageHandler<unknown>]),
            });

            // Notify about new subscription
            notifySubscriptionsChange();

            // Return a subscription that removes this specific handler when canceled
            return createTopicSubscription(topicName, onMessage as TopicMessageHandler<unknown>);
        } catch (error) {
            console.error(`Failed to subscribe to topic ${topicName}:`, error);
            throw error;
        }
    };

    const cancelAllSubscriptions = (): void => {
        subscriptions.forEach((sub, topic) => {
            try {
                console.debug(`Cancelling subscription to topic ${topic}`);
                sub.subscription.cancel();
            } catch (error) {
                console.error(`Error cancelling subscription to ${topic}:`, error);
            }
        });

        subscriptions.clear();
        notifySubscriptionsChange();
    };

    return {
        subscribe,
        cancelAllSubscriptions,
        getMessageCount,
        setMessageCountCallback,
        getCurrentSubscriptions,
    };
}
